import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Pill } from "./MemberPersonalFields";

const FITNESS_GOALS = [
  ["weight_loss", "Weight loss"], ["muscle_gain", "Muscle gain"], ["general_fitness", "General fitness"],
  ["endurance", "Endurance"], ["flexibility", "Flexibility & mobility"],
];
const FITNESS_LEVELS = [["beginner", "Beginner"], ["intermediate", "Intermediate"], ["advanced", "Advanced"]];
const MEDICAL_CONDITIONS = [
  ["diabetes", "Diabetes"], ["hypertension", "Hypertension"], ["heart", "Heart condition"],
  ["asthma", "Asthma"], ["joint", "Joint / back issues"],
];
const PREV_GYM_EXPERIENCE = ["None", "<1 year", "1–3 years", "3+ years"];
const WORKOUT_TIMES = ["Early morning", "Morning", "Afternoon", "Evening", "Night"];
const REFERRAL_SOURCES = ["Walk-in", "Friend/family referral", "Social media", "Google search", "Flyer/ad", "Other"];

const EMPTY_HEALTH_FORM = {
  fitnessGoal: "", fitnessLevel: "", medicalHistory: "", medicalConditions: [],
  currentMedications: "", knownAllergies: "", prevGymExperience: "", preferredWorkoutTime: "", referralSource: "",
};

// Step 3 ("Health & goals") — shared by Walk-in and Add-family. Entirely optional (Skip always
// available); everything captured here rides on the member's already-encrypted, freeform
// `medical_flags` dict (models_commercial.py) rather than needing its own schema.
export function HealthGoalsFields({ member, onDone, pageTestId = DESK.walkinHealthPage }) {
  const [form, setForm] = useState(EMPTY_HEALTH_FORM);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCondition = (c) => setForm((f) => ({
    ...f, medicalConditions: f.medicalConditions.includes(c) ? f.medicalConditions.filter((x) => x !== c) : [...f.medicalConditions, c],
  }));

  const saveMutation = useMutation({
    mutationFn: () => api.patch(`/members/${member.id}`, {
      medical_flags: {
        fitness_goal: form.fitnessGoal || null, fitness_level: form.fitnessLevel || null,
        medical_history: form.medicalHistory || null, medical_conditions: form.medicalConditions,
        current_medications: form.currentMedications || null, known_allergies: form.knownAllergies || null,
        prev_gym_experience: form.prevGymExperience || null, preferred_workout_time: form.preferredWorkoutTime || null,
        referral_source: form.referralSource || null,
      },
    }),
    onSuccess: () => { toast.success("Health & goals saved."); onDone(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={pageTestId} className="space-y-5">
      <div className="space-y-1.5">
        <Label>Fitness goal <span className="text-xs font-normal" style={{ color: "#68686E" }}>(optional)</span></Label>
        <div className="flex gap-2 flex-wrap">
          {FITNESS_GOALS.map(([v, l]) => (
            <Pill key={v} testId={DESK.fitnessGoalPill(v)} active={form.fitnessGoal === v} onClick={() => set("fitnessGoal", form.fitnessGoal === v ? "" : v)}>{l}</Pill>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Fitness level <span className="text-xs font-normal" style={{ color: "#68686E" }}>(optional)</span></Label>
        <div className="flex gap-2">
          {FITNESS_LEVELS.map(([v, l]) => (
            <Pill key={v} testId={DESK.fitnessLevelPill(v)} active={form.fitnessLevel === v} onClick={() => set("fitnessLevel", form.fitnessLevel === v ? "" : v)}>{l}</Pill>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Medical history <span className="text-xs font-normal" style={{ color: "#68686E" }}>(optional)</span></Label>
        <Textarea data-testid={DESK.medicalHistoryInput} value={form.medicalHistory} onChange={(e) => set("medicalHistory", e.target.value)} placeholder="Any past injuries, surgeries, or conditions we should know about" />
      </div>

      <div className="space-y-1.5">
        <Label>Medical conditions <span className="text-xs font-normal" style={{ color: "#68686E" }}>(optional, select any)</span></Label>
        <div className="flex gap-2 flex-wrap">
          {MEDICAL_CONDITIONS.map(([v, l]) => (
            <Pill key={v} testId={DESK.medicalConditionPill(v)} active={form.medicalConditions.includes(v)} onClick={() => toggleCondition(v)}>{l}</Pill>
          ))}
        </div>
      </div>

      <FormFieldTextarea label="Current medications" testId={DESK.currentMedicationsInput} value={form.currentMedications} onChange={(v) => set("currentMedications", v)} />
      <FormFieldTextarea label="Known allergies" testId={DESK.knownAllergiesInput} value={form.knownAllergies} onChange={(v) => set("knownAllergies", v)} />

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Previous gym experience</Label>
          <Select onValueChange={(v) => set("prevGymExperience", v)} value={form.prevGymExperience || undefined}>
            <SelectTrigger data-testid={DESK.prevGymExperienceSelect}><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>
              {PREV_GYM_EXPERIENCE.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Preferred workout time</Label>
          <Select onValueChange={(v) => set("preferredWorkoutTime", v)} value={form.preferredWorkoutTime || undefined}>
            <SelectTrigger data-testid={DESK.preferredWorkoutTimeSelect}><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>
              {WORKOUT_TIMES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>How did they hear about us?</Label>
        <Select onValueChange={(v) => set("referralSource", v)} value={form.referralSource || undefined}>
          <SelectTrigger data-testid={DESK.referralSourceSelect}><SelectValue placeholder="Optional" /></SelectTrigger>
          <SelectContent>
            {REFERRAL_SOURCES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="outline" data-testid={DESK.enrollMemberSkipButton} onClick={onDone}>Skip</Button>
        <Button
          data-testid={DESK.enrollMemberButton} disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()} className="text-white flex-1" style={{ background: "#D6202B" }}
        >
          {saveMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : "Finish"}
        </Button>
      </div>
    </div>
  );
}

function FormFieldTextarea({ label, testId, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label} <span className="text-xs font-normal" style={{ color: "#68686E" }}>(optional)</span></Label>
      <Textarea data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
